/**
 * EffectiveSLP — kid-facing practice player offline attempt queue.
 *
 * The player batches scored trials into an IndexedDB object store
 * keyed by client-generated `id` (UUID v4). On flush, we POST the
 * batch to `api-record-practice-attempts` (idempotent on the
 * server side via the `id` primary key).
 *
 * Triggers (per AC #9):
 *   - on activity completion (caller invokes `flushNow()`)
 *   - on `visibilitychange` to hidden (auto-listener installed
 *     by `installPracticeQueueListeners`)
 *   - every 30s if the queue is non-empty
 *   - on `online` event (browser regains network)
 *
 * Quota (per AC #12 + the plan doc): the daily cap is 5 attempts
 * x 5 items x ~10 trials = 250 rows/day per child_identifier_hash.
 * iOS Safari caps IndexedDB at ~50MB which is far above what we
 * actually consume. The "Saving your work..." surface (AC #12)
 * fires when the queue is over 1000 pending rows — also a non-
 * realistic threshold but matches the plan doc.
 *
 * Lives in its own module (not inline in ChildPracticePlayer.tsx)
 * so the .tsx file stays components-only — that satisfies the
 * `react/only-export-components` oxlint rule the rest of web/src
 * follows.
 */

/**
 * One row in the IndexedDB queue. Mirrors the server-side
 * `practice_attempts` insert shape in api-record-practice-attempts.
 * The `id` is a client-generated UUID; the server uses it as the
 * primary key for idempotency (re-sends are no-ops).
 */
export interface QueuedAttempt {
  id: string;
  practice_set_item_id: string;
  session_local_id: string;
  attempted_at: string; // ISO timestamp
  trial_index: number;
  prompt_kind: 'picture' | 'audio' | 'text' | 'mixed';
  expected_response: unknown;
  child_response: unknown;
  is_correct: boolean;
  response_latency_ms: number;
  self_corrected: boolean;
  used_cue: boolean;
  client_metadata: unknown;
}

/**
 * Per-session summary the server expects alongside the attempt
 * batch. Mirrors `PracticeSession` in api-record-practice-attempts.
 */
export interface SessionSummary {
  id: string;
  started_at: string;
  ended_at: string;
  trials_attempted: number;
  trials_correct: number;
  activities_completed: number;
  bail_trial_index: number | null;
  device_fingerprint_hash: string;
}

/**
 * Payload we POST to api-record-practice-attempts.
 */
export interface FlushPayload {
  token: string;
  child_identifier_hash: string;
  nickname: string;
  attempts: QueuedAttempt[];
  session: SessionSummary;
}

/**
 * Persisted row shape — wraps a QueuedAttempt with the queue
 * metadata so we can audit/inspect during dev. The `payload` is
 * the full POST body we'd send to the server.
 */
interface QueueRow {
  /** Client-generated UUID; used as the IndexedDB keyPath. */
  id: string;
  /** Wall-clock ISO timestamp for when the row was enqueued. */
  enqueued_at: string;
  /**
   * Cumulative count of consecutive 4xx (permanent) rejections
   * the server has returned for this row. Incremented by
   * `flushQueue` on each non-2xx response and reset to 0 on
   * success. Network errors and 5xx do NOT increment this —
   * those are transient and should keep being retried without
   * counting against the row's permanent-failure budget.
   *
   * Missing on rows written before the counter was added; we
   * treat absent as 0 for back-compat.
   */
  permanent_failure_count?: number;
  /** The POST payload; the server recurses `attempts` to the queue id. */
  payload: FlushPayload;
}

const DB_NAME = 'effectiveslp-practice';
const DB_VERSION = 1;
const STORE_NAME = 'practice_attempts';

/** Threshold for the "Saving your work..." blocking surface (AC #12). */
export const QUEUE_BACKPRESSURE_THRESHOLD = 1000;

/**
 * Auto-flush interval when the queue is non-empty.
 */
export const FLUSH_INTERVAL_MS = 30_000;

/**
 * Dead-letter eviction threshold (issue #294).
 *
 * After this many consecutive 4xx rejections from the server, the
 * row is dropped from the queue — it's poisoned (e.g. hash_mismatch
 * or invalid_item won't ever succeed on retry) and leaving it would
 * grow queue depth monotonically until the AC #12 backpressure
 * gate permanently blocks the kid from practicing.
 *
 * Network errors and 5xx do NOT count toward this budget; they are
 * transient and keep being retried.
 */
export const MAX_PERMANENT_FAILURE_RETRIES = 3;

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (): void => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void =>
      reject(req.error ?? new Error('indexeddb_open_failed'));
  });
  return dbPromise;
}

/**
 * Enqueue a single attempt row. `clientId` must be a UUID v4
 * generated by the caller (the player generates one per trial —
 * or per batch — and reuses the same id on retry, so server-side
 * idempotency dedupes).
 *
 * Returns the persisted `id` so the caller can reference it.
 */
export async function enqueueAttempt(
  clientId: string,
  payload: FlushPayload,
): Promise<string> {
  const db = await openDb();
  const row: QueueRow = {
    id: clientId,
    enqueued_at: new Date().toISOString(),
    payload,
  };
  return new Promise<string>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(row);
    tx.oncomplete = (): void => resolve(clientId);
    tx.onerror = (): void =>
      reject(tx.error ?? new Error('indexeddb_put_failed'));
  });
}

/**
 * Read every queued attempt, sorted by `enqueued_at` ascending (oldest
 * first). Used by the auto-flush loop and by the manual `flushNow` caller.
 *
 * Note: `IDBObjectStore.getAll()` returns rows ordered by primary key,
 * and our keyPath is a client-generated UUID v4, so the raw IDB order
 * is effectively random. `flushQueue()` (and any future caller that
 * inspects chronological ordering) relies on this function returning
 * insertion order, so we sort explicitly here rather than at every
 * call site (#297).
 */
export async function readAllQueued(): Promise<QueueRow[]> {
  const db = await openDb();
  const rows = await new Promise<QueueRow[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).getAll();
    req.onsuccess = (): void => resolve((req.result ?? []) as QueueRow[]);
    req.onerror = (): void =>
      reject(req.error ?? new Error('indexeddb_getall_failed'));
  });
  // ISO 8601 timestamps sort lexicographically; ascending => oldest first.
  rows.sort((a, b) => (a.enqueued_at < b.enqueued_at ? -1 : a.enqueued_at > b.enqueued_at ? 1 : 0));
  return rows;
}

/**
 * Drop rows whose ids are in `ids`. Called after a successful
 * server POST so the queue doesn't re-send on the next flush.
 */
export async function removeQueued(ids: readonly string[]): Promise<void> {
  if (ids.length === 0) return;
  const db = await openDb();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const id of ids) {
      store.delete(id);
    }
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void =>
      reject(tx.error ?? new Error('indexeddb_delete_failed'));
  });
}

/**
 * Count queued rows. Drives the "Saving your work..." surface
 * (AC #12) — when the count exceeds QUEUE_BACKPRESSURE_THRESHOLD
 * the player should block new activity until the queue drains.
 */
export async function queueDepth(): Promise<number> {
  const db = await openDb();
  return new Promise<number>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).count();
    req.onsuccess = (): void => resolve(req.result);
    req.onerror = (): void =>
      reject(req.error ?? new Error('indexeddb_count_failed'));
  });
}

/**
 * Update the cached session summary on every queued row for a given
 * session. Called by the player when an activity finishes so the
 * flushed payload carries the final `activities_completed` count even
 * though the queued attempt rows were written before the boundary.
 */
export async function updateSessionSummary(
  sessionId: string,
  summary: SessionSummary,
): Promise<void> {
  const db = await openDb();
  const rows = await readAllQueued();
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    for (const row of rows) {
      if (row.payload.session.id === sessionId) {
        store.put({
          ...row,
          payload: {
            ...row.payload,
            session: { ...summary, id: sessionId },
          },
        });
      }
    }
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void =>
      reject(tx.error ?? new Error('indexeddb_update_session_failed'));
  });
}

/**
 * Flush every queued row by POSTing to `endpointUrl`. Returns
 * the count of rows successfully flushed (rows that 200'd and
 * were removed).
 *
 * Per-row failure handling (issue #294):
 *   - 2xx → evict the rows. (`permanent_failure_count` is gone
 *     with the row, so no separate reset is needed.)
 *   - 4xx → bump each row's `permanent_failure_count`. If the
 *     row reaches `MAX_PERMANENT_FAILURE_RETRIES`, evict it as
 *     a dead-letter — the server will never accept it (e.g.
 *     `hash_mismatch`, `invalid_item`) and leaving it would
 *     grow queue depth monotonically until the AC #12
 *     backpressure gate permanently blocks the kid.
 *   - 5xx + network errors → leave rows in the queue; they are
 *     transient and should keep being retried without burning
 *     the row's permanent-failure budget.
 *
 * Skips the network entirely when `navigator.onLine === false`;
 * returns 0 in that case so the caller can show the
 * "Saving your work…" state without spinning.
 */
export async function flushQueue(
  endpointUrl: string,
  options: { anonKey: string },
): Promise<{ flushed: number; remaining: number }> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    const remaining = await queueDepth();
    return { flushed: 0, remaining };
  }
  const rows = await readAllQueued();
  if (rows.length === 0) {
    return { flushed: 0, remaining: 0 };
  }

  // Group rows by their session id so each POST contains one
  // session's full attempt batch. Rows from different sessions in
  // the same queue are unusual (a reload mid-activity) but we
  // handle them.
  const bySession = new Map<string, QueueRow[]>();
  for (const row of rows) {
    const sessionId = row.payload.session.id;
    const bucket = bySession.get(sessionId);
    if (bucket) {
      bucket.push(row);
    } else {
      bySession.set(sessionId, [row]);
    }
  }

  let flushed = 0;
  /** Rows to drop from the queue after this flush (success or poisoned). */
  const evictIds = new Set<string>();
  /** Per-row permanent-failure counter updates to persist. */
  const bumpedCounts = new Map<string, number>();
  for (const bucket of bySession.values()) {
    const first = bucket[0];
    const last = bucket[bucket.length - 1];
    if (!first || !last) continue;

    // Bucket is now sorted oldest -> newest by `enqueued_at` (see
    // `readAllQueued`), so `last` is the chronologically most-recent
    // row for this session, not just an arbitrary UUID-sorted one
    // (#297). But we still don't trust `last.payload.session` for
    // window boundaries — `updateSessionSummary()` only fires on
    // activity completion, so any flush that lands between activities
    // (30s interval, `visibilitychange`, `online`) can carry stale
    // `ended_at` / `activities_completed` from an earlier write.
    // Merge explicitly: earliest start, latest end, max activity count.
    const startedAt = bucket.reduce<string>(
      (acc, r) => (r.payload.session.started_at < acc ? r.payload.session.started_at : acc),
      bucket[0]!.payload.session.started_at,
    );
    const endedAt = bucket.reduce<string>(
      (acc, r) => (r.payload.session.ended_at > acc ? r.payload.session.ended_at : acc),
      bucket[0]!.payload.session.ended_at,
    );
    const activitiesCompleted = bucket.reduce<number>(
      (acc, r) =>
        r.payload.session.activities_completed > acc
          ? r.payload.session.activities_completed
          : acc,
      0,
    );
    // `bail_trial_index` is set by the player when the kid bails
    // mid-trial; propagate the latest non-null value so an earlier
    // trial's null doesn't clobber a later bail.
    const bailTrialIndex = bucket.reduce<number | null>(
      (acc, r) => {
        const candidate = r.payload.session.bail_trial_index;
        if (candidate === null || candidate === undefined) return acc;
        if (acc === null) return candidate;
        return candidate > acc ? candidate : acc;
      },
      null,
    );
    const trialsAttempted = bucket.length;
    const trialsCorrect = bucket.filter(
      (r) => r.payload.attempts[0]?.is_correct,
    ).length;
    const merged: FlushPayload = {
      ...last.payload,
      session: {
        ...last.payload.session,
        started_at: startedAt,
        ended_at: endedAt,
        activities_completed: activitiesCompleted,
        bail_trial_index: bailTrialIndex,
        trials_attempted: trialsAttempted,
        trials_correct: trialsCorrect,
      },
      attempts: bucket.map((r) => ({
        id: r.id,
        practice_set_item_id: r.payload.attempts[0]?.practice_set_item_id ?? '',
        session_local_id: r.payload.session.id,
        attempted_at: r.payload.attempts[0]?.attempted_at ?? r.enqueued_at,
        trial_index: r.payload.attempts[0]?.trial_index ?? 0,
        prompt_kind: r.payload.attempts[0]?.prompt_kind ?? 'picture',
        expected_response: r.payload.attempts[0]?.expected_response ?? null,
        child_response: r.payload.attempts[0]?.child_response ?? null,
        is_correct: r.payload.attempts[0]?.is_correct ?? false,
        response_latency_ms:
          r.payload.attempts[0]?.response_latency_ms ?? 0,
        self_corrected: r.payload.attempts[0]?.self_corrected ?? false,
        used_cue: r.payload.attempts[0]?.used_cue ?? false,
        client_metadata: r.payload.attempts[0]?.client_metadata ?? {},
      })),
    };
    try {
      const res = await fetch(endpointUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          apikey: options.anonKey,
          authorization: `Bearer ${options.anonKey}`,
        },
        body: JSON.stringify(merged),
      });
      if (res.ok) {
        flushed += bucket.length;
        // Success: drop the rows from the queue. We don't bother
        // resetting `permanent_failure_count` because the row is
        // being deleted in this same flush.
        for (const r of bucket) {
          evictIds.add(r.id);
        }
        continue;
      }
      if (res.status >= 400 && res.status < 500) {
        // Permanent client error (hash_mismatch, invalid_item, etc.).
        // Bump each row's counter and evict any that have hit the
        // dead-letter threshold — otherwise the queue would grow
        // monotonically and trip the AC #12 backpressure gate,
        // permanently blocking the kid from practicing (#294).
        for (const r of bucket) {
          const next = (r.permanent_failure_count ?? 0) + 1;
          if (next >= MAX_PERMANENT_FAILURE_RETRIES) {
            evictIds.add(r.id);
          } else {
            bumpedCounts.set(r.id, next);
          }
        }
        continue;
      }
      // 5xx — transient server error. Leave rows in the queue
      // unchanged; the next flush will retry.
    } catch {
      // Network error — same as 5xx: leave rows in the queue so
      // the next online/visibility/30s flush retries them.
    }
  }

  if (bumpedCounts.size > 0) {
    // Apply counter updates + drop the rows we marked for eviction.
    const db = await openDb();
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const [id, count] of bumpedCounts) {
        // Re-read the row so we don't clobber enqueued_at / payload.
        const getReq = store.get(id);
        getReq.onsuccess = (): void => {
          const row = getReq.result as QueueRow | undefined;
          if (!row) return;
          store.put({ ...row, permanent_failure_count: count });
        };
      }
      tx.oncomplete = (): void => resolve();
      tx.onerror = (): void =>
        reject(tx.error ?? new Error('indexeddb_bump_failed'));
    });
  }
  if (evictIds.size > 0) {
    await removeQueued(Array.from(evictIds));
  }

  const remaining = await queueDepth();
  return { flushed, remaining };
}

/**
 * Install the auto-flush listeners. Call once when the player
 * mounts and tear down with the returned disposer.
 *
 *  - `visibilitychange` to hidden → flush
 *  - `online` event → flush
 *  - 30s setInterval when the queue is non-empty → flush
 *
 * The interval is cheap; the actual queueDepth() check happens
 * inside flushQueue(). Safe to call multiple times across
 * remounts — the disposer cleans up.
 */
export function installPracticeQueueListeners(
  endpointUrl: string,
  options: { anonKey: string },
): () => void {
  if (typeof window === 'undefined') {
    return (): void => {};
  }
  const onVis = (): void => {
    if (document.visibilityState === 'hidden') {
      void flushQueue(endpointUrl, options);
    }
  };
  const onOnline = (): void => {
    void flushQueue(endpointUrl, options);
  };
  const interval = window.setInterval(() => {
    void flushQueue(endpointUrl, options);
  }, FLUSH_INTERVAL_MS);
  document.addEventListener('visibilitychange', onVis);
  window.addEventListener('online', onOnline);
  return (): void => {
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('online', onOnline);
    window.clearInterval(interval);
  };
}